/**
 * Two zoom spaces coexist in CreativeZoom:
 *
 * 1. Lib transform scale (react-zoom-pan-pinch)
 *    `1` = image at the size CSS already fitted into the panel
 *    (`object-fit: contain`). Zoom in/out multiplies from there.
 *
 * 2. Native scale (what the badge shows)
 *    `1` = 1 CSS px ≈ 1 px of the source file — useful for reading small
 *    legal copy. A 1920px banner fitted to ~800px starts around 0.42× even
 *    when the lib reports 1.
 *
 * Bridge: native = libTransform × cssFitRatio
 * where cssFitRatio = renderedWidth / naturalWidth.
 *
 * The badge click inverts that: ask the lib for `1 / cssFitRatio` so
 * native lands on 1×.
 */
export const cssFitRatio = (
  renderedWidth: number,
  naturalWidth: number
): number | null => {
  if (!renderedWidth || !naturalWidth) return null

  return renderedWidth / naturalWidth
}

/** Badge value: lib transform expressed as native (file-pixel) zoom. */
export const toNativeScale = (
  libTransformScale: number,
  renderedWidth: number,
  naturalWidth: number
) => {
  const fit = cssFitRatio(renderedWidth, naturalWidth)

  if (fit == null) return libTransformScale

  return libTransformScale * fit
}

/**
 * Lib transform scale that makes native zoom 1×.
 * Pass this to `centerView` when the badge is clicked.
 */
export const libScaleForNative = (
  renderedWidth: number,
  naturalWidth: number
): number | null => {
  const fit = cssFitRatio(renderedWidth, naturalWidth)

  if (fit == null) return null

  return 1 / fit
}
